import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pensionParamsApi, F938 } from '../../../services/pensionParamsApi';

export type Mode = 'browse' | 'add' | 'edit';

export function usePensionForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F938[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [wage, setWage] = useState(0);
  const [rate, setRate] = useState(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await pensionParamsApi.list(1, 200); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setWage(current.f938_01); setRate(current.f938_12); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };
  const startAdd = () => { setMode('add'); setWage(0); setRate(0); };
  const startEdit = () => { if (!current) return; setMode('edit'); setWage(current.f938_01); setRate(current.f938_12); };
  const cancel = () => { setMode('browse'); if (current) { setWage(current.f938_01); setRate(current.f938_12); } };

  const save = async () => {
    try {
      if (mode === 'add') { await pensionParamsApi.create({ f938_01: wage, f938_12: rate }); message.success('新增成功'); }
      else { await pensionParamsApi.update(current!.f938_01, { f938_12: rate }); message.success('修改成功'); }
      const data = await loadRecords();
      const t = mode === 'add' ? wage : current!.f938_01;
      const idx = data.findIndex(r => r.f938_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await pensionParamsApi.delete(current.f938_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const val = parseInt(t, 10);
    if (isNaN(val)) { message.warning('請輸入數字'); return; }
    const exact = records.findIndex(r => r.f938_01 === val);
    if (exact >= 0) { setCurrentIdx(exact); return; }
    const near = records.findIndex(r => r.f938_01 >= val);
    if (near >= 0) { setCurrentIdx(near); message.info(`定位至最近的 ${records[near].f938_01}`); }
    else { message.warning('查無此提繳工資等級'); }
  };

  // BR-C01 preview
  const dailyPreview = wage * rate / 100 / 30;
  const monthlyPreview = wage * rate / 100;

  return { mode, isEditing, loading, records, current, currentIdx,
    wage, setWage, rate, setRate, dailyPreview, monthlyPreview,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
